import { MutationTree } from 'vuex';
import {
  IContractManagementFilter,
  IContractManagementState,
} from '@/contract-management/store/contract-management.store';
import { IContractManagementFilterRanges } from '@/contract-management/types/contract-management-filter-ranges';
import { IContractManagementKeyValues } from '@/contract-management/types/contract-management-key-values';
import { IDocument } from '@/types/Document';
import { IFolder } from '@/folders/types/folder';
import { ITag } from '@/types/Tag';
import { ISorting } from '@/types/Sorting';
import { IDocumentNotification } from '@/types/DocumentNotification';
import { IFolderTree } from '@/folders/types/folder-tree';

export const mutationError = 'error';
export const mutationLoadingDocuments = 'loadingDocuments';

export const mutationSetFilter = 'setFilter';
export const mutationSetFilterRanges = 'setFilterRanges';
export const mutationSetDefaultFilter = 'setDefaultFilter';
export const mutationSetFilterActiveState = 'setFilterActiveState';

export const mutationSetDocuments = 'setDocuments';
export const mutationSetFolders = 'setFolders';
export const mutationSetSelectedFolder = 'setSelectedFolder';
export const mutationSetBreadcrumb = 'setBreadcrumb';
export const mutationSetKeyValues = 'setKeyValues';
export const mutationOpenFolder = 'openFolder';
export const mutationSetAllTags = 'setAllTags';
export const mutationDeleteDocuments = 'deleteDocuments';
export const mutationEditDocuments = 'editDocuments';
export const mutationAddTag = 'addTag';
export const mutationAddDocumentTags = 'addDocumentTags';
export const mutationUpdateFolder = 'updateFolder';
export const mutationDeleteFolder = 'deleteFolder';
export const mutationUpdateSorting = 'updateSorting';
export const mutationLoadFolderTree = 'loadFolderTree';
export const mutationSetDocumentNotifications = 'setDocumentNotifications';
export const mutationAddDocumentNotifications = 'addDocumentNotifications';
export const mutationDeleteDocumentNotifications = 'deleteDocumentNotification';
export const mutationToggleSelectDocument = 'toggleSelectDocument';
export const mutationToggleSelectAllDocuments = 'toggleSelectAllDocuments';
export const mutationDeselectAllDocuments = 'deselectAllDocuments';

export const mutations: MutationTree<IContractManagementState> = {
  [mutationSetDocumentNotifications](
    state: IContractManagementState,
    documentNotifications: IDocumentNotification[]
  ) {
    state.documentNotifications = documentNotifications;
  },
  [mutationAddDocumentNotifications](
    state: IContractManagementState,
    documentNotifications: IDocumentNotification[]
  ) {
    state.documentNotifications = [...state.documentNotifications, ...documentNotifications];
  },
  [mutationDeleteDocumentNotifications](state: IContractManagementState, ids: string[]) {
    state.documentNotifications =
      state.documentNotifications.length > 0
        ? state.documentNotifications.filter(
            (documentNotification: IDocumentNotification) =>
              !ids.some((id: string) => id === documentNotification.id)
          )
        : state.documentNotifications;
  },
  [mutationUpdateSorting](state: IContractManagementState, sorting: ISorting) {
    state.filter.sortBy = sorting.sortBy;
    state.filter.sortOrder = sorting.sortOrder;
  },
  [mutationError](state: IContractManagementState, error: string) {
    state.error = error;
  },
  [mutationDeleteDocuments](state: IContractManagementState) {
    state.documents = state.documents.filter(
      (document: IDocument) => !state.selectedDocumentIds.includes(document.id)
    );
  },
  [mutationEditDocuments](state: IContractManagementState, payload) {
    state.documents = state.documents.map((document: IDocument) =>
      state.selectedDocumentIds.includes(document.id)
        ? {
            ...document,
            value: payload.value || document.value,
            contractEndDate: payload.contractEndDate || document.contractEndDate,
            contractStartDate: payload.contractStartDate || document.contractStartDate,
            user: payload.user || document.user,
          }
        : document
    );
  },
  [mutationLoadingDocuments](state: IContractManagementState, payload: boolean) {
    state.isLoadingDocuments = payload;
  },
  [mutationSetFilter](state: IContractManagementState, payload: IContractManagementFilter) {
    state.filter = payload;
  },
  [mutationSetDefaultFilter](state: IContractManagementState, payload: IContractManagementFilter) {
    state.filter = payload;
    state.defaultFilter = payload;
    state.filterInitialized = true;
  },
  [mutationSetDocuments](state: IContractManagementState, documents: IDocument[]) {
    state.documents = documents;
    state.hasLoadedDocuments = true;
  },
  [mutationSetFolders](state: IContractManagementState, folders: IFolder[]) {
    state.folders = folders;
  },
  [mutationSetSelectedFolder](
    state: IContractManagementState,
    { folder, folders }: { folder: IFolder; folders: IFolder[] }
  ) {
    state.selectedFolder = folder;
    state.selectedFolder.folders = folders;
  },
  [mutationSetBreadcrumb](state: IContractManagementState, breadcrumb: IFolder[]) {
    state.breadcrumb = breadcrumb;
  },
  [mutationSetKeyValues](state: IContractManagementState, keyValues: IContractManagementKeyValues) {
    state.keyValues = keyValues;
  },
  [mutationOpenFolder](state: IContractManagementState, folderId: string) {
    state.folderId = folderId;
  },
  [mutationSetFilterRanges](
    state: IContractManagementState,
    filterRanges: IContractManagementFilterRanges
  ) {
    state.filterRanges = filterRanges;
  },
  [mutationSetFilterActiveState](state: IContractManagementState, isFilterActive: boolean) {
    state.filterActive = isFilterActive;
  },
  [mutationSetAllTags](state: IContractManagementState, allTags: ITag[]) {
    state.allTags = allTags;
  },
  [mutationAddTag](state: IContractManagementState, newTag: ITag) {
    if (state.allTags !== undefined) {
      state.allTags = [...state.allTags, newTag];
      return;
    }
    state.allTags = [newTag];
  },
  [mutationAddDocumentTags](state: IContractManagementState, taggedDocuments: IDocument[]) {
    state.documents = state.documents.map((document: IDocument) =>
      taggedDocuments.some((taggedDocument: IDocument) => taggedDocument.id === document.id)
        ? {
            ...document,
            tags: taggedDocuments.find(
              (taggedDocument: IDocument) => document.id === taggedDocument.id
            ).tags,
          }
        : document
    );
  },
  [mutationUpdateFolder](state: IContractManagementState, folder: IFolder) {
    state.folders = state.folders.map((stateFolder: IFolder) =>
      stateFolder.id === folder.id ? { ...stateFolder, name: folder.name } : stateFolder
    );
  },
  [mutationDeleteFolder](state: IContractManagementState, folderId: string) {
    state.folders = state.folders.filter((stateFolder: IFolder) => stateFolder.id !== folderId);
  },
  [mutationLoadFolderTree](state: IContractManagementState, folderTree: IFolderTree) {
    state.folderTree = folderTree;
  },
  [mutationToggleSelectDocument](state: IContractManagementState, documentId: string) {
    state.selectedDocumentIds = state.selectedDocumentIds.includes(documentId)
      ? state.selectedDocumentIds.filter((id: string) => id !== documentId)
      : [...state.selectedDocumentIds, documentId];
  },
  [mutationToggleSelectAllDocuments](state: IContractManagementState) {
    state.selectedDocumentIds =
      state.selectedDocumentIds.length === state.documents.length
        ? []
        : state.documents.map((document: IDocument) => document.id);
  },
  [mutationDeselectAllDocuments](state: IContractManagementState) {
    state.selectedDocumentIds = [];
  },
};
